import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  ANALYTICS_PAGE_NAMES,
  API_CATEGORY_TYPES,
  AppConstant,
  CUSTOM_ANALYTICS_EVENT_NAMES,
  fetchMedicalCalculator,
  getAnalyticPageNameByCategory,
  getAnalyticsUserDetails,
  getLoggedInUserInfo,
  getMedicalCalculator,
  ImageConstant,
  logEvent,
  PageNames,
  PlatformNames,
  translate,
  useAppDispatch,
  useAppSelector
} from '@shalina-app/shared';

import { CalculatorProps } from '.';
import styles from './calculator.module.scss';
import {
  BarLoader,
  CustomTabs,
  QuickLinkSection
} from '../../../../components';
import { CriticalCriteria } from './components'; // use CriticalCriteria for all tabs here
import { MEDICAL_CALCULATOR_TABS } from '../../../../app.constants';
import { retrieveData, STORAGE_CONSTANTS, UserInterface } from '../../../../utils';
import { SignUpPopup } from '../../../Authentication';
import { appInsights } from '../../../../applicationInsight';

export const Calculator: React.FC<CalculatorProps> = () => {
  const t = translate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const tab = location?.state?.tab;

  const isLoggedIn: boolean = retrieveData(STORAGE_CONSTANTS.IS_LOGGED_IN);
  const user: UserInterface | any = retrieveData(STORAGE_CONSTANTS.USER);
  const STORAGE_KEY = `downloadedIds`
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [openCalcDetail, setopenCalcDetail] = useState(false);
  const [currentActiveItem, setCurrentActiveItem] = useState('');
  const [apiCall, setApiCall] = useState(false);
  const [selectedInterest, setSelectedInterest] = useState<Array<string>>([]);
  const [criticalCriteria, setCriticalCriteria] = useState<any>([]);
  const [decisionTrees, setDecisionTrees] = useState<any>([]);
  const [medicalEquation, setMedicalEquation] = useState<any>([]);
  const [dataFromStore, setDataFromStore] = useState(true);
  const [showSignUp, setShowSignUp] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  // 2. Use lazy-loaded state
  const [downloadedIds, setDownloadedIds] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      const parsed = saved ? JSON.parse(saved) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });

  const content = useAppSelector(getMedicalCalculator);
  const userInfo = useAppSelector(getLoggedInUserInfo);
  const hasLoggedOnce = useRef(false);

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          setDownloadedIds(parsed);
        }
      } catch (err) {
        console.warn('Error parsing downloadedIds from localStorage:', err);
      }
    }
  }, [STORAGE_KEY]);

  // 3. Auto-save to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(downloadedIds));
  }, [downloadedIds, STORAGE_KEY]);

  useEffect(() => {
    if (hasLoggedOnce.current) return;
    hasLoggedOnce.current = true;

    logEvent(appInsights, 'pageView', ANALYTICS_PAGE_NAMES.MEDICAL_CALCULATOR);
    if (isLoggedIn && user) {
      const userObj = userInfo || user;
      const userData = getAnalyticsUserDetails(userObj);

      logEvent(appInsights, 'custom', CUSTOM_ANALYTICS_EVENT_NAMES.USER_MOST_PAGE_VIEWED, {
        userEmail: userData?.emailId,
        userMobile: userData?.mobile,
        userName: userData?.name,
        userQualification: userData?.qualification,
        userInterest: userData?.areaOfInterest,
        userSpecialization: userData?.specialization,
        userCountryOfPractice: userData?.countryOfPractice,
        userPreferredLanguage: userData?.preferredLanguage,
        moduleName: getAnalyticPageNameByCategory(API_CATEGORY_TYPES.MEDICAL_CALCULATORS) || '',
        pageName: `${getAnalyticPageNameByCategory(API_CATEGORY_TYPES.MEDICAL_CALCULATORS)} ${PageNames?.LISTING}`,
        platform: PlatformNames.WEB,
      });
    }
  }, []);

  useEffect(() => {
    if (tab) {
      window.scrollTo(0, 0);
      setTabValue(tab);
    }
  }, [tab]);

  useEffect(() => {
    if (apiCall) {
      loadApi();
      setApiCall(false);
    }
  }, [apiCall]);

  useEffect(() => {
    if (content) {
      for (const key in content) {
        if (key === 'Clinical Criteria') setCriticalCriteria(content[key]);
        if (key === 'Decision Trees') setDecisionTrees(content[key]);
        if (key === 'Medical Equations') setMedicalEquation(content[key]);
      }
      setDataFromStore(false);
    }
  }, [content]);

  const loadApi = async () => {
    const response = await dispatch(fetchMedicalCalculator({ interest: selectedInterest.toString() })).unwrap();
    setDataFromStore(false);

    if (response?.status === 'SUCCESS' && response?.statusCode === 200 && response?.data) {
      if (response.data.length === 0) {
        setCriticalCriteria([]);
        setMedicalEquation([]);
        setDecisionTrees([]);
      } else {
        for (const key in response.data) {
          if (key === 'Clinical Criteria') setCriticalCriteria(response.data[key]);
          if (key === 'Decision Trees') setDecisionTrees(response.data[key]);
          if (key === 'Medical Equations') setMedicalEquation(response.data[key]);
        }
      }
    }
  };

  const handleDownloadCalculator = async (url: string, name: string, tid: string) => {
    if (downloadedIds.includes(tid)) return;

    try {
      setDownloadingId(tid);
      const response = await fetch(url);
      const blob = await response.blob();

      const anchor = document.createElement('a');
      const blobUrl = URL.createObjectURL(blob);
      anchor.href = blobUrl;
      anchor.download = name ? `${name}.html` : 'calculator.html';
      anchor.click();

      setTimeout(() => {
        setDownloadedIds(prev => [...prev, tid]);
        setDownloadingId(null);
        URL.revokeObjectURL(blobUrl);
      }, 500);
    } catch (error) {
      console.error('Download failed:', error);
      setDownloadingId(null);
    }
  };

  const handleArrowClick = (url: string, name: string = '') => {
    if (isLoggedIn) {
      setUrl(url);
      setCurrentActiveItem(name);
      setOpen(true);
      setopenCalcDetail(true);
    } else {
      setShowSignUp(true);
      setopenCalcDetail(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setopenCalcDetail(false);
    setCurrentActiveItem('');
  };

  const handleTabChange = (event: React.SyntheticEvent, value: number) => {
    setTabValue(value);
    setCurrentActiveItem('');
  };

  const tabs = [
    {
      id: MEDICAL_CALCULATOR_TABS.CLINICAL_CRITERIA,
      label: t(AppConstant.COMMON.CLINICAL_CRITERIA_TAB),
      component: (
        <CriticalCriteria
          handleArrowClick={handleArrowClick}
          data={criticalCriteria}
          guestUser={!isLoggedIn}
          isOffline={navigator?.onLine === false}
          handleDownload={handleDownloadCalculator}
          downloadingId={downloadingId}
          downloadedIds={downloadedIds}
        />
      )
    },
    {
      id: MEDICAL_CALCULATOR_TABS.DECISION_TREES,
      label: t(AppConstant.COMMON.DECISION_TREES_TAB),
      component: (
        <CriticalCriteria
          handleArrowClick={handleArrowClick}
          data={decisionTrees}
          guestUser={!isLoggedIn}
          isOffline={navigator?.onLine === false}
          handleDownload={handleDownloadCalculator}
          downloadingId={downloadingId}
          downloadedIds={downloadedIds}
        />
      )
    },
    {
      id: MEDICAL_CALCULATOR_TABS.MEDICAL_EQUATIONS,
      label: t(AppConstant.COMMON.MEDICAL_EQUATIONS_TAB),
      component: (
        <CriticalCriteria
          handleArrowClick={handleArrowClick}
          data={medicalEquation}
          guestUser={!isLoggedIn}
          isOffline={navigator?.onLine === false}
          handleDownload={handleDownloadCalculator}
          downloadingId={downloadingId}
          downloadedIds={downloadedIds}
        />
      )
    }
  ];

  return (
    <>
      <div className={`${styles.calcTop} ${currentActiveItem && styles.calcDetail}`}>
        <div className='cust-container'>
          <nav aria-label='breadcrumb' className={styles.breadWrapper}>
            <ol className={`${styles.breadcrumb} ${!currentActiveItem && styles.hasDarkBg}`}>
              <li>
                <a onClick={handleClose} style={{ cursor: 'pointer', borderBottom: '1px' }}>
                  {tabs[tabValue]?.label}
                </a>
              </li>
              {currentActiveItem && (
                <li>
                  <span className={styles.bredArrow}>
                    <svg width='7' height='11' viewBox='0 0 7 11' fill='none'>
                      <path
                        d='M1 1L6 5.5L0.999999 10'
                        stroke='#002E5B'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  </span>
                  <span className={styles.breadcrumbCurrent}>{currentActiveItem}</span>
                </li>
              )}
            </ol>
          </nav>
          {!currentActiveItem && <h1 className={styles.calcHead}>{t(AppConstant.HEADER.MEDICAL_CALCULATOR_MENU)}</h1>}
        </div>
      </div>

      {dataFromStore && <BarLoader />}

      <div className='cust-container'>
        <div className={styles.calcRow}>
          <div className={styles.calcCol8}>
            {dataFromStore ? null : criticalCriteria.length === 0 && decisionTrees.length === 0 && medicalEquation.length === 0 ? (
              <div className={styles.contentNotFound}>
                <div className={styles.notFoundImg}>
                  <img src={ImageConstant.SOURCE.contentNotFoundImg} alt='not found' />
                </div>
                <div className={styles.notFoundText}>
                  <h3>{`${t(AppConstant.HOME_PAGE.NO_TEXT)} ${t(AppConstant.HOME_PAGE.DATA_TEXT)} ${t(AppConstant.HOME_PAGE.FOUND_TEXT)}`}</h3>
                  <p>{t(AppConstant.HOME_PAGE.NO_DATA_FOUND_MESSAGE)}</p>
                </div>
              </div>
            ) : (
              <div className={styles.calcPageWrapper}>
                {!openCalcDetail ? (
                  <div className={styles.shalinaTabs}>
                    <CustomTabs tabs={tabs} tabValue={tabValue} handleTabChange={handleTabChange} />
                  </div>
                ) : (
                  <div className='testDic'>
                    <object data={url} className={styles.iframe}></object>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className={styles.calcCol4}>
            <div className={styles.calcQuicklink}>
              <QuickLinkSection enumIds={['bookmark', 'aboutUs']} />
            </div>
          </div>
        </div>
      </div>

      <SignUpPopup open={showSignUp} handleClose={() => setShowSignUp(false)} />
    </>
  );
};
